/**
 * Push notification subscription utility
 * Web Push API를 사용한 푸시 알림 구독/해제
 */
import { apiUrl } from '../services/api';

// URL-safe base64 → Uint8Array 변환
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

let vapidPublicKey = null;

async function getVapidKey() {
  if (vapidPublicKey) return vapidPublicKey;
  try {
    const res = await fetch(apiUrl('/notifications/push/vapid-key'));
    const data = await res.json();
    if (data.success && data.publicKey) {
      vapidPublicKey = data.publicKey;
      return vapidPublicKey;
    }
  } catch (e) {
    console.warn('VAPID 키 로드 실패:', e);
  }
  return null;
}

/**
 * 푸시 알림 구독 시도 (자동)
 * - SW ready 후 호출
 * - 이미 구독이 있으면 서버에 재등록만 함
 * - 구독이 없으면 권한 요청 후 구독
 */
export async function subscribePush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;

  try {
    const registration = await navigator.serviceWorker.ready;

    // 기존 구독 확인
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // VAPID 공개키 가져오기
      const key = await getVapidKey();
      if (!key) return false;

      // 권한 요청
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return false;

      // 새 구독 생성
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
    }

    // 서버에 구독 정보 전송
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    await fetch(apiUrl('/notifications/push/subscribe'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ subscription: subscription.toJSON() }),
    });

    return true;
  } catch (e) {
    console.warn('푸시 구독 실패:', e);
    return false;
  }
}

/**
 * 푸시 알림 구독 해제
 */
export async function unsubscribePush() {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await fetch(apiUrl('/notifications/push/unsubscribe'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint }),
      });
    }
  } catch (e) {
    console.warn('푸시 구독 해제 실패:', e);
  }
}

/**
 * 현재 푸시 구독 상태 확인
 */
export async function isPushSubscribed() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}
